import to from 'await-to-js';
import { Dispatch, SetStateAction } from 'react';
import { SetterOrUpdater, useRecoilState } from 'recoil';
import { CustomerTier } from '../models/CustomerTier.model';
import { CustomerViewModel } from '../models/CustomerView.model';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { customerIsFetchingState, customerState } from '../recoil/atoms';
import { HoneyloveApi, useHoneyloveAuthedApi } from './useHoneyloveAuthedApi';

declare const window: HoneyloveWindow;

export const tierText = (tier: CustomerTier): string => {
  switch (tier) {
    case 2:
      return 'Queen Bee**';
    case 1:
      return 'Honey Bee*';
    case 0:
    default:
      return 'Hive Member';
  }
};

let useCustomerFetched = false;

const fetchCustomer = async (
  honeyloveApi: HoneyloveApi,
  set: SetterOrUpdater<CustomerViewModel> | Dispatch<SetStateAction<CustomerViewModel>>,
  setIsFetching: Dispatch<SetStateAction<boolean>>,
  forceResync = false,
) => {
  setIsFetching(true);
  const [err, customerResponse] = await to(honeyloveApi.apiGet<CustomerViewModel>(`api/profile/user?forceResync=${forceResync}`));

  if (err) {
    window.Sentry.captureException(err);
  }

  if (customerResponse) {
    const customerFromResponse = {
      ...customerResponse,
      totalPoints: customerResponse.activities.reduce((acc, curr) => acc + curr.pointsGranted, 0),
    };
    set(customerFromResponse);
  }

  setIsFetching(false);
};

export const useCustomer = () => {
  const honeyloveApi = useHoneyloveAuthedApi();
  const [customer, setCustomer] = useRecoilState(customerState);
  const [isFetching, setIsFetching] = useRecoilState(customerIsFetchingState);

  if (!useCustomerFetched) {
    useCustomerFetched = true;
    fetchCustomer(honeyloveApi, setCustomer, setIsFetching);
  }

  const refetch = () => fetchCustomer(honeyloveApi, setCustomer, setIsFetching);
  const resync = () => fetchCustomer(honeyloveApi, setCustomer, setIsFetching, true);

  return { customer, refetch, resync, isFetching };
};
